import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Button, Form, Line, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useCustomDeadline, { useCustomDeadlineForm } from '@hooks/useCustomDeadline'

import { confirmDelete } from '@functions/entity'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import Deadlines from './components/Deadlines'
import EmailRecipients from './components/EmailRecipients'

const MODAL_KEY = 'CreateOrEditCustomDeadlineModal'

type ModalCallbacks = {
  closeModal: () => void,
  createCustomDeadline: (payload: HandleCallbackActionParams) => void,
  updateCustomDeadline: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditCustomDeadlineModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createCustomDeadline, updateCustomDeadline } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const customDeadline = digObject(modalPayload, 'customDeadline', {})

  const newRecord = !customDeadline.id

  const {
    callbacks: {
      createCustomDeadline: createFn,
      deleteCustomDeadline: deleteFn,
      updateCustomDeadline: updateFn,
    },
    creating,
    deleting,
    updating,
  } = useCustomDeadline(customDeadline)

  const {
    entityState,
    entityState: {
      deadline_type,
      description,
      enabled_days,
      occurs_daily,
      title,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = useCustomDeadlineForm(customDeadline, { validateOn: 'change' })

  const createOrUpdateCustomDeadlinePayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: ` ${newRecord ? 'Created' : 'Updated'}`,
  }

  const confirmDeletePayload = {
    callbacks: {
      action: deleteFn,
      afterAction: closeModal,
    },
    strings: { entityName: 'Custom Deadline' },
  }

  const saveButtonEnabled = saveEnabled
  && (deadline_type !== 'once' && String(occurs_daily) === 'false' ? !!enabled_days.length : true)

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title={newRecord ? 'Create' : 'Edit'}
        titleSecondLine="Custom Deadlines"
      />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Title" errorMessage={errors.title}>
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <Form.Field label="* Description" marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <textarea
              name="description"
              style={{ minHeight: 100, resize: 'vertical' }}
              value={description}
              {...handlers}
            />
          </Form.Field>

          <Form.Field label="* Occurs (Schedule)" marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <select name="deadline_type" value={deadline_type} {...handlers}>
              <option value="once">Once Off</option>
              <option value="weekly">Every Week</option>
              <option value="fortnightly">Every Fortnight</option>
              <option value="monthly">Every Month</option>
            </select>
          </Form.Field>

          <Deadlines entityState={entityState} handlers={handlers} setEntityState={setEntityState} />

          <EmailRecipients entityState={entityState} setEntityState={setEntityState} />

          {!newRecord && (
            <>
              <Line style={{ marginBottom: 0 }} />

              <Form.Field label="More Options" marginTop="large">
                <Button
                  buttonStyle="secondaryUtility"
                  icon={<FontAwesomeIcon icon={faTrashAlt} />}
                  loading={deleting}
                  // @ts-expect-error TS2345
                  onClick={() => confirmDelete(confirmDeletePayload)}
                  size="medium"
                >
                  Delete Custom Deadline
                </Button>
              </Form.Field>
            </>
          )}

        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveButtonEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              ? () => createCustomDeadline(createOrUpdateCustomDeadlinePayload)
              : () => updateCustomDeadline(createOrUpdateCustomDeadlinePayload)
          }
          size="large"
        >
          {saveButtonEnabled ? `${newRecord ? 'Create' : 'Update'} ` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditCustomDeadlineModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
